/**
 * ApprovalProgressMini — poziomy stepper postępu instancji procesu (F17 T17.1.8b).
 *
 * Kółka ze statusowym glifem (✓ zatwierdzony / ● zakończony wyborem drogi — neutralnie, z nazwą
 * drogi / zegar = bieżący|oczekuje / − pominięty / ⚠ błąd; każda decyzja `approve` — z drogą
 * albo bez niej — dostaje ● zakończony; krok zamknięty „Cofnij do poprzedniego etapu" to też ●,
 * z podpisem „Cofnięto"), połączone strzałkami (lewo→prawo =
 * kolejność chronologiczna kroków; array order = `createdAt` z backendu). Zastępuje gęstą
 * `ProcessStepsTable` w banerze — pełna historia kroków jest w `ProcessHistoryTab` (miejsca na
 * kartach — nagłówek tamtego pliku).
 *
 * „Bieżący" zależy WYŁĄCZNIE od statusu kroku (`pending`/`in_progress`) — NIE od
 * `currentNodeIds`: gdy droga prowadzi z powrotem do wcześniejszego etapu, węzeł jest odwiedzany
 * ponownie, więc `nodeId` nie jest unikalny i stary (zakończony) krok fałszywie wyglądałby na
 * „bieżący".
 * Równoległość → kilka kroków `in_progress` naraz (kilka „bieżących" kropek).
 * Przy wielu krokach zwija się środek: widoczne są pierwsze kroki, ostatnie kroki i ZAWSZE
 * każdy krok „w toku" (równoległość — nie znika z paska); każdy ciągły odcinek ukrytych kroków
 * to osobny znacznik „+k", kolejność chronologiczna bez zmian — bez paginacji.
 * Nazwa etapu pod kółkiem jest obcinana do `8rem` (wielokropek), więc niesie `title` z pełną
 * nazwą (dymek po najechaniu) — tak samo jak linijka statusu z nazwą drogi.
 *
 * Odłożone (PHASE_17_PLAN §T17.1.8b): rozwijane stacked-linie gałęzi równoległych.
 */

import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

import type { InstanceStep } from '@/lib/processes/instance-api';
import {
  type RouteSnapshot,
  stepRouteName,
  type Tone,
  TONE_DOT,
  TONE_ICON,
  toneOf,
  wasSteppedBack,
} from '@/lib/processes/step-tone';

export interface ApprovalProgressMiniProps {
  /** Kroki instancji w kolejności wykonania (array order = `createdAt` z backendu). */
  steps: InstanceStep[];
  /**
   * Zamrożony graf sprawy — z niego bierzemy NAZWĘ drogi, którą zakończył się krok (zamiast
   * ogólnego „Zakończony"). Bez grafu krok zamknięty drogą pokazuje samo „Zakończony".
   */
  snapshot?: RouteSnapshot | null;
  testIdPrefix?: string;
}

/** Ile kroków pokazujemy na krańcach, gdy zwijamy środek (head + „+N" + tail). */
const HEAD = 1;
const TAIL = 4;

const TONE_FALLBACK: Record<Tone, string> = {
  approved: 'Zatwierdzony',
  done: 'Zakończony',
  current: 'W toku',
  skipped: 'Pominięty',
  error: 'Błąd',
  pending: 'Oczekuje',
};

export function ApprovalProgressMini({
  steps,
  snapshot = null,
  testIdPrefix = 'process-stepper',
}: ApprovalProgressMiniProps): ReactElement {
  const { t } = useTranslation();

  // Elementy paska w kolejności chronologicznej: krok albo znacznik zwiniętego odcinka. Bez
  // zwijania — same kroki. Zwijając: widoczne są pierwsze HEAD, ostatnie TAIL i WSZYSTKIE kroki
  // „w toku"; każdy ciągły odcinek ukrytych kroków to osobny znacznik „+k".
  const collapse = steps.length > HEAD + TAIL;
  const items: Array<
    { kind: 'step'; step: InstanceStep } | { kind: 'gap'; key: string; count: number }
  > = [];
  steps.forEach((s, i) => {
    const visible = !collapse || i < HEAD || i >= steps.length - TAIL || toneOf(s) === 'current';
    if (visible) {
      items.push({ kind: 'step', step: s });
      return;
    }
    const last = items[items.length - 1];
    if (last?.kind === 'gap') {
      last.count += 1;
    } else {
      items.push({ kind: 'gap', key: `gap-${s.stepInstanceId}`, count: 1 });
    }
  });

  const renderStep = (step: InstanceStep): ReactElement => {
    const tone = toneOf(step);
    const isCurrent = tone === 'current';
    const Icon = TONE_ICON[tone];
    // Krok zamknięty drogą: pod kółkiem NAZWA wybranej drogi (np. „Odrzuć"), a gdy jej nie znamy —
    // ogólne „Zakończony". Krok zamknięty cofnięciem: „Cofnięto" (cofnięcie nie ma drogi, a głos
    // za drogą oddany wcześniej na tym kroku nie jest tym, co go zamknęło). Pozostałe tony —
    // zawsze ich własny napis.
    const toneText = t(`tenant.processes.stepper.tone.${tone}`, TONE_FALLBACK[tone]);
    const steppedBack = tone === 'done' && wasSteppedBack(step);
    const routeName = tone === 'done' && !steppedBack ? stepRouteName(step, snapshot) : null;
    const statusText = steppedBack
      ? t('tenant.processes.stepper.steppedBack', 'Cofnięto')
      : (routeName ?? toneText);
    return (
      <div
        role="listitem"
        data-testid={`${testIdPrefix}-step`}
        data-tone={tone}
        data-current={isCurrent ? 'true' : 'false'}
        className="flex min-w-0 flex-col items-center text-center"
      >
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${TONE_DOT[tone]}`}
          aria-hidden="true"
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
        <span
          title={step.label || undefined}
          className="mt-1 max-w-[8rem] truncate text-xs font-medium text-text-primary"
        >
          {step.label ?? t('tenant.processes.stepper.noLabel', 'Krok')}
        </span>
        <span
          data-testid={`${testIdPrefix}-step-status`}
          title={routeName ?? undefined}
          className="max-w-[8rem] truncate text-[10px] text-text-secondary"
        >
          {statusText}
        </span>
      </div>
    );
  };

  // Strzałka kierunku (lewo→prawo = kolejność). Wyrównana pionowo do środka kółka (h-6).
  const arrow = (
    <ChevronRight
      aria-hidden="true"
      className="mt-1.5 h-4 w-4 shrink-0 text-text-tertiary"
      strokeWidth={2.5}
    />
  );

  // `div role="list"` zamiast `<ol>` — strzałki/connectory nie są poprawnymi dziećmi `<ol>`.
  return (
    <div
      role="list"
      data-testid={testIdPrefix}
      aria-label={t('tenant.processes.stepper.aria', 'Postęp procesu akceptacji')}
      className="flex items-start gap-1 overflow-x-auto"
    >
      {items.map((item, i) =>
        item.kind === 'step' ? (
          <Fragment key={item.step.stepInstanceId}>
            {i > 0 ? arrow : null}
            {renderStep(item.step)}
          </Fragment>
        ) : (
          <Fragment key={item.key}>
            {i > 0 ? arrow : null}
            <div
              role="listitem"
              data-testid={`${testIdPrefix}-collapsed`}
              className="flex flex-col items-center text-center text-text-secondary"
              title={t('tenant.processes.stepper.collapsedTitle', 'Ukryte kroki pośrednie')}
            >
              <span className="flex h-6 items-center text-sm" aria-hidden="true">
                …
              </span>
              <span className="text-[10px]">+{item.count}</span>
            </div>
          </Fragment>
        ),
      )}
    </div>
  );
}
